import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance, FormHelper } from "utils/helpers";
import useFloatingMessages from "../messages/useFloatingMessages";
import { FormikProps } from "formik";
import { FormConstants } from "utils/constants";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";

function useFormSubmitHelpers() {
  const { enqueueMessage } = useFloatingMessages();

  const showValidationErrorMessage = (
    validationFailMessage?: string,
    validationFailMessageType?: StyleEnums.DisplayType,
    autoHide = true
  ) => {
    if (validationFailMessage) {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(validationFailMessage),
        validationFailMessageType ?? StyleEnums.DisplayType.Danger,
        autoHide
      );
    }
  };

  // Surface the first specific error, but only translation keys ("Foo.Bar") -- avoids Yup's un-keyed
  // English defaults leaking as raw text, and lets required-field errors use the localized fallback.
  const getValidationFailMessage = (errors: any, fallbackMessage?: string) => {
    const specificMessage = FormHelper.getErrorMessages(errors).find(
      (message) => message !== TranslationKeys.Validations.Required && /^\w+(\.\w+)+$/.test(message)
    );
    return specificMessage ?? fallbackMessage;
  };

  const validateForm = async (
    form: FormikProps<any>,
    validationFailMessage?: string,
    validationSchema?: ObjectSchema<any>,
    validationFailMessageType?: StyleEnums.DisplayType,
    autoHideValidationFailMessage = true,
    onFormValidationError?: () => void
  ) => {
    const values = form.values;
    form.setStatus(FormConstants.Submitted);

    let isValid = true;
    let errors: any = {};
    if (validationSchema) {
      try {
        await FormHelper.validateYupSchema(values, validationSchema);
        isValid = true;
      } catch (validationErrors) {
        errors = FormHelper.yupToFormErrors(validationErrors);
        form.setErrors(errors);
        form.setTouched(await FormHelper.getTouchedObj(errors, form.touched));
        isValid = false;
      }
    } else {
      errors = await form.validateForm();
      const touched = await FormHelper.getTouchedObj(errors, form.touched);
      form.setTouched(touched);
      isValid = Object.keys(errors).length === 0;
    }

    if (!isValid) {
      form.setSubmitting(false);
      showValidationErrorMessage(
        getValidationFailMessage(errors, validationFailMessage),
        validationFailMessageType,
        autoHideValidationFailMessage
      );
      onFormValidationError && onFormValidationError();
      // eslint-disable-next-line no-throw-literal
      throw null;
    }
    return isValid;
  };

  const submitForm = async (
    form: FormikProps<any>,
    submitFunc: (values: any, data?: any) => Promise<any>,
    validationSchema?: ObjectSchema<any>,
    noValidation?: boolean,
    data?: any,
    customValidationFailMessage?: string,
    validationFailMessageType?: StyleEnums.DisplayType,
    autoHideValidationFailMessage?: boolean,
    onFormValidationError?: () => void
  ) => {
    form.setSubmitting(true);
    form.setStatus(FormConstants.Submitted);

    await form.submitForm();

    const isValid =
      noValidation ||
      (await validateForm(
        form,
        customValidationFailMessage ?? TranslationKeys.Validations.FormSubmitFailed,
        validationSchema,
        validationFailMessageType,
        autoHideValidationFailMessage,
        onFormValidationError
      ));

    const values = form.values;

    if (isValid) {
      try {
        const res = await submitFunc(values, data);
        return res;
      } catch (error) {
        throw error;
      } finally {
        form.setSubmitting(false);
      }
    }
  };

  return { submitForm, validateForm };
}

export default useFormSubmitHelpers;
